/**
 * Apten Widget Loader
 *
 * This loader script runs in the parent page and creates an iframe
 * to host the actual widget. It handles configuration, positioning,
 * and communication with the widget iframe.
 */

import type { WidgetConfig } from "../shared/config";
import { IframeManager } from "./iframe-manager";
import { WidgetCommunication } from "./communication";
import { debug } from "./utils/debug";
import {
  validateWidget,
  createSession,
  AuthenticationError,
  endSession,
  signalInactive,
} from "./auth";
import { calculateBodySHA256 } from "./utils/crypto";
import type { AuthValidationResponse, AuthResponse } from "./auth";
import {
  setStorageItem,
  getStorageItem,
  removeStorageItem,
} from "../widget/utils/storage";

// Global state
let iframeManager: IframeManager | null = null;
let communication: WidgetCommunication | null = null;
let isInitializing = false;

// Multi-tab and page lifecycle state
let broadcastChannel: BroadcastChannel | null = null;
const instanceId = crypto.randomUUID();
let pagehideHandler: (() => void) | null = null;
let precomputedInactiveSha256: string | null = null;
let isTabPaused = false;

// WebSocket (AppSync Events) state
let eventsWebSocket: WebSocket | null = null;
let intentionalClose = false;
let reconnectAttempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let currentEventsConfig: {
  httpEndpoint: string;
  realtimeEndpoint: string;
} | null = null;
let currentSessionId: string | null = null;
let onWebchatEvent: ((event: any) => void) | null = null;
let getCurrentSessionToken: (() => string | null) | null = null;

// Global widget configuration
declare global {
  interface Window {
    AptenWidget?: WidgetConfig;
    AptenWidgetAPI?: {
      initialize: (config: WidgetConfig) => Promise<void>;
      reinitialize: () => Promise<void>;
      cleanup: () => void;
    };
  }
}

/**
 * Validate and normalize widget configuration
 */
function validateConfig(config: WidgetConfig): WidgetConfig {
  // Add API key fallback for development
  let finalConfig = { ...config };

  if (!finalConfig.apiKey && import.meta.env.VITE_DEV_API_KEY) {
    finalConfig.apiKey = import.meta.env.VITE_DEV_API_KEY;
    debug.log(
      "[Apten Loader] Using development API key from environment variable"
    );
  }

  if (!finalConfig.apiKey) {
    throw new Error(
      "API key is required. Please provide an apiKey in the configuration or set VITE_DEV_API_KEY in .env.local"
    );
  }

  // Set defaults
  finalConfig.theme = finalConfig.theme || "light";
  finalConfig.position = finalConfig.position || "bottom-right";

  if (finalConfig.header) {
    const text =
      typeof finalConfig.header.text === "string"
        ? finalConfig.header.text.trim()
        : undefined;

    finalConfig.header = {
      ...(text ? { text } : {}),
    };
  }

  if (finalConfig.launcher) {
    const launcher = finalConfig.launcher;
    const text =
      typeof launcher.text === "string" ? launcher.text.trim() : undefined;
    const shape =
      launcher.shape === "circle" ||
      launcher.shape === "square" ||
      launcher.shape === "rounded"
        ? launcher.shape
        : undefined;

    finalConfig.launcher = {
      content:
        launcher.content === "logo" || launcher.content === "text"
          ? launcher.content
          : "icon",
      ...(text ? { text } : {}),
      ...(typeof launcher.showIcon === "boolean"
        ? { showIcon: launcher.showIcon }
        : {}),
      ...(shape ? { shape } : {}),
    };
  }

  return finalConfig;
}

/**
 * Save session ID to parent page localStorage
 */
function saveSessionId(sessionId: string): void {
  setStorageItem("session-id", sessionId);
  debug.log("[Apten Loader] Session ID saved to parent storage:", sessionId);
}

/**
 * Clear session ID from parent page localStorage
 */
function clearSessionId(): void {
  removeStorageItem("session-id");
  debug.log("[Apten Loader] Session ID cleared from parent storage");
}

/**
 * Attempt to restore session from parent localStorage
 * Returns restored session data if successful, null if should create new session
 */
async function attemptSessionRestoration(
  apiKey: string
): Promise<AuthResponse | null> {
  const storedSessionId = getStorageItem<string | null>("session-id", null);

  if (!storedSessionId) {
    debug.log("[Session Restoration] No session ID found in storage");
    return null;
  }

  debug.log(
    "[Session Restoration] Attempting to restore session:",
    storedSessionId
  );

  try {
    const restoredSession = await createSession(apiKey, storedSessionId);

    if (restoredSession.newSession) {
      // Server created new session instead of restoring (shouldn't happen with valid session)
      debug.log(
        "[Session Restoration] Server created new session instead of restoring"
      );
      return restoredSession;
    } else {
      debug.log("[Session Restoration] ✅ Session restored successfully:", {
        sessionId: restoredSession.sessionId,
        messageCount: restoredSession.chatHistory?.length || 0,
      });
      return restoredSession;
    }
  } catch (error) {
    debug.log("[Session Restoration] ❌ Restoration failed:", error);

    // Parse error response for specific handling
    if (error instanceof AuthenticationError) {
      if (error.status === 410) {
        debug.log("[Session Restoration] Session expired");
        // TODO: Future - show expiration dialog
      } else if (error.status === 404) {
        debug.log("[Session Restoration] Session not found");
      } else if (error.status === 403) {
        debug.log("[Session Restoration] Session access denied");
      }
    }

    // Clear invalid session ID from storage
    clearSessionId();

    return null; // Signal to create new session
  }
}

/**
 * Clean up existing widget instances
 */
function cleanup(): void {
  debug.log("[Apten Loader] Cleaning up existing widget...");

  if (broadcastChannel) {
    broadcastChannel.close();
    broadcastChannel = null;
  }
  if (pagehideHandler) {
    window.removeEventListener("pagehide", pagehideHandler);
    pagehideHandler = null;
  }
  isTabPaused = false;
  precomputedInactiveSha256 = null;
  closeEventsWebSocket();
  onWebchatEvent = null;
  getCurrentSessionToken = null;

  if (communication) {
    communication.destroy();
    communication = null;
  }
  if (iframeManager) {
    iframeManager.destroy();
    iframeManager = null;
  }

  // Additional cleanup: remove any orphaned elements
  const selectors = [
    "#apten-widget-container",
    "#apten-widget-error",
    ".apten-widget-container",
    ".apten-widget-error",
    '[data-widget="apten"]',
    '[id*="apten-widget"]',
  ];

  selectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((element) => {
      debug.log(`[Apten Loader] Removing orphaned element: ${selector}`);
      element.remove();
    });
  });
}

/**
 * Initialize the widget with the provided configuration
 */
async function initializeWidget(config: WidgetConfig): Promise<void> {
  if (isInitializing) {
    debug.log(
      "[Apten Loader] Initialization already in progress, skipping..."
    );
    return;
  }

  isInitializing = true;

  try {
    debug.log("[Apten Loader] Starting widget initialization...");

    // Clean up existing widget
    cleanup();

    // Validate configuration
    const validatedConfig = validateConfig(config);

    debug.log("[Apten Loader] Configuration validated:", {
      apiKey: validatedConfig.apiKey.substring(0, 10) + "...",
      theme: validatedConfig.theme,
      position: validatedConfig.position,
    });

    // PHASE 1: Lightweight validation (no session creation)
    debug.log("[Apten Loader] Phase 1: Validating API key...");
    const validation = await validateWidget(validatedConfig.apiKey);

    debug.log("[Apten Loader] API key valid, creating widget...");

    // Create iframe (starts hidden)
    iframeManager = new IframeManager(validatedConfig);
    const iframe = iframeManager.createIframe();

    // Set up communication
    communication = new WidgetCommunication(iframe);

    // Forward mobile/desktop viewport crossings to the widget so it can
    // adjust its internal layout (data-viewport attribute, visualViewport
    // listener, etc.). Fires on rotation and window resize.
    iframeManager.onViewportChange((isMobile) => {
      communication!.sendToWidget({
        type: "VIEWPORT_CHANGE",
        payload: { isMobile },
      });
    });

    // Set up communication handlers for two-phase flow
    setupCommunicationHandlers(validatedConfig, validation);

    // Show the widget now (without session)
    debug.log("[Apten Loader] Showing widget...");
    iframeManager!.showWidget();

    debug.log(
      "[Apten Loader] Widget iframe created and communication established"
    );
  } catch (error) {
    debug.error("[Apten Loader] Failed to initialize widget:", error);

    // Show error in development
    if (import.meta.env.VITE_DEBUG === "true") {
      const errorContainer = document.createElement("div");
      errorContainer.id = "apten-widget-error";
      errorContainer.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        background: #ff4444;
        color: white;
        padding: 10px;
        border-radius: 4px;
        font-size: 12px;
        z-index: 999999;
        max-width: 300px;
      `;
      errorContainer.textContent = `[DEV] Loader Error: ${
        error instanceof Error ? error.message : String(error)
      }`;
      document.body.appendChild(errorContainer);
    }

    // Clean up on error
    cleanup();
  } finally {
    isInitializing = false;
  }
}

/**
 * Connect to AppSync Events WebSocket for real-time messages
 */
function connectEventsWebSocket(
  eventsConfig: { httpEndpoint: string; realtimeEndpoint: string },
  sessionId: string
): void {
  const sessionToken = getCurrentSessionToken?.();
  if (!sessionToken) {
    debug.warn("[Apten Loader] No session token available, skipping WebSocket connect");
    return;
  }
  // Clean up existing connection without resetting reconnect counter
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (eventsWebSocket) {
    intentionalClose = true; // Prevent old onclose from triggering reconnect
    eventsWebSocket.close();
    eventsWebSocket = null;
  }

  currentEventsConfig = eventsConfig;
  currentSessionId = sessionId;
  intentionalClose = false;

  try {
    // Extract HTTP API host for auth headers (AppSync requires appsync-api host, not appsync-realtime-api)
    // httpEndpoint may be a full URL or just a domain
    let httpHost: string;
    try {
      httpHost = new URL(eventsConfig.httpEndpoint).host;
    } catch {
      // Not a full URL — use as-is (strip trailing slashes/paths)
      httpHost = eventsConfig.httpEndpoint.replace(/\/.*$/, "");
    }

    debug.log("[Apten Loader] Connecting WebSocket to:", {
      realtimeEndpoint: eventsConfig.realtimeEndpoint,
      httpEndpoint: eventsConfig.httpEndpoint,
      httpHost,
      sessionId,
    });

    const headerObj = {
      host: httpHost,
      Authorization: sessionToken,
    };
    const b64Header = btoa(JSON.stringify(headerObj));

    const ws = new WebSocket(
      `wss://${eventsConfig.realtimeEndpoint}/event/realtime`,
      [`header-${b64Header}`, "aws-appsync-event-ws"]
    );

    ws.onopen = () => {
      debug.log("[Apten Loader] WebSocket connected, sending connection_init");
      ws.send(JSON.stringify({ type: "connection_init" }));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        switch (msg.type) {
          case "connection_ack":
            debug.log("[Apten Loader] WebSocket connection_ack, subscribing");
            ws.send(
              JSON.stringify({
                type: "subscribe",
                id: crypto.randomUUID(),
                channel: `/chat/session/${sessionId}`,
                authorization: {
                  Authorization: sessionToken,
                  host: httpHost,
                },
              })
            );
            break;

          case "subscribe_success":
            debug.log("[Apten Loader] WebSocket subscribed to session channel");
            reconnectAttempts = 0;
            break;

          case "data": {
            const events = Array.isArray(msg.event)
              ? msg.event
              : [msg.event];
            for (const eventStr of events) {
              const parsed =
                typeof eventStr === "string"
                  ? JSON.parse(eventStr)
                  : eventStr;
              onWebchatEvent?.(parsed);
            }
            break;
          }

          case "ka":
            // Keep-alive, no action needed
            break;

          case "error":
            debug.error("[Apten Loader] WebSocket error event:", msg);
            break;
        }
      } catch (e) {
        debug.error("[Apten Loader] WebSocket message parse error:", e);
      }
    };

    ws.onclose = (closeEvent) => {
      // Ignore close events from replaced WebSocket instances
      if (ws !== eventsWebSocket) return;

      debug.log("[Apten Loader] WebSocket closed", {
        intentional: intentionalClose,
        code: closeEvent.code,
        reason: closeEvent.reason,
        wasClean: closeEvent.wasClean,
      });
      if (!intentionalClose) {
        scheduleReconnect();
      }
    };

    ws.onerror = () => {
      debug.error("[Apten Loader] WebSocket connection error");
    };

    eventsWebSocket = ws;
  } catch (e) {
    debug.error("[Apten Loader] Failed to create WebSocket:", e);
  }
}

/**
 * Close the AppSync Events WebSocket intentionally
 */
function closeEventsWebSocket(): void {
  intentionalClose = true;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (eventsWebSocket) {
    eventsWebSocket.close();
    eventsWebSocket = null;
  }
  reconnectAttempts = 0;
}

/**
 * Schedule a WebSocket reconnection with exponential backoff
 */
function scheduleReconnect(): void {
  if (reconnectAttempts >= 5) {
    debug.warn(
      "[Apten Loader] WebSocket reconnect failed after 5 attempts, giving up"
    );
    return;
  }

  const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
  reconnectAttempts++;

  debug.log(
    `[Apten Loader] WebSocket reconnecting in ${delay}ms (attempt ${reconnectAttempts}/5)`
  );

  reconnectTimer = setTimeout(() => {
    if (currentEventsConfig && currentSessionId) {
      connectEventsWebSocket(
        currentEventsConfig,
        currentSessionId
      );
    }
  }, delay);
}

/**
 * Set up communication handlers for two-phase authentication flow
 */
function setupCommunicationHandlers(
  config: WidgetConfig,
  validation: AuthValidationResponse
): void {
  let sessionCreated = false;
  let currentSessionToken: string | null = null; // Track current session token

  // Expose session token getter for WebSocket reconnection
  getCurrentSessionToken = () => currentSessionToken;

  // Set up WebSocket event handler (uses closure vars)
  onWebchatEvent = (event: any) => {
    if (event.type === "MESSAGE") {
      debug.log("[Apten Loader] WebSocket MESSAGE received:", {
        sender: event.sender,
        messageId: event.messageId,
      });
      communication?.sendToWidget({
        type: "WEBCHAT_EVENT",
        payload: event,
      });
    }
    if (event.type === "SESSION_TERMINATED") {
      debug.log(
        "[Apten Loader] WebSocket SESSION_TERMINATED:",
        event.reason
      );
      communication?.sendToWidget({
        type: "SESSION_TERMINATED_SERVER",
        payload: { reason: event.reason || "terminated" },
      });
      closeEventsWebSocket();
      currentSessionToken = null;
      clearSessionId();
      sessionCreated = false;
    }
  };

  // Set up BroadcastChannel for multi-tab coordination
  try {
    broadcastChannel = new BroadcastChannel("apten-webchat-session");
    broadcastChannel.onmessage = (event) => {
      const { type, claimerInstanceId } = event.data;
      if (type === "SESSION_CLAIMED" && claimerInstanceId !== instanceId) {
        debug.log(
          "[Apten Loader] Another tab claimed session, pausing this tab"
        );
        sessionCreated = false; // Allow re-creation via "Resume here"
        isTabPaused = true; // Prevent pagehide from sending INACTIVE
        closeEventsWebSocket(); // Paused tab doesn't need real-time events
        communication?.sendToWidget({ type: "PAUSED", payload: {} });
      }
    };
  } catch (e) {
    debug.warn(
      "[Apten Loader] BroadcastChannel not supported, multi-tab detection disabled"
    );
  }

  // Handle session creation request from widget
  communication!.onMessage("REQUEST_SESSION", async () => {
    if (sessionCreated) {
      debug.log(
        "[Apten Loader] Session already created, ignoring duplicate request"
      );
      return; // Prevent duplicate calls
    }

    try {
      debug.log("[Apten Loader] Phase 2: Attempting session restoration...");

      // Try to restore existing session first
      const restoredSession = await attemptSessionRestoration(config.apiKey);

      let authResponse: AuthResponse;

      if (restoredSession) {
        // Use restored session
        authResponse = restoredSession;
        debug.log("[Apten Loader] Using restored session");
      } else {
        // Create new session
        debug.log("[Apten Loader] Creating new session...");
        authResponse = await createSession(config.apiKey);
      }

      sessionCreated = true;
      currentSessionToken = authResponse.sessionToken; // Store current session token
      saveSessionId(authResponse.sessionId);

      // Claim session across tabs and re-enable pagehide listener
      isTabPaused = false;
      broadcastChannel?.postMessage({
        type: "SESSION_CLAIMED",
        claimerInstanceId: instanceId,
        sessionId: authResponse.sessionId,
      });

      // Pre-compute SHA256 for INACTIVE signal (body is always the same)
      calculateBodySHA256(JSON.stringify({ sessionStatus: "INACTIVE" })).then(
        (sha) => {
          precomputedInactiveSha256 = sha;
        }
      );

      // Register pagehide listener (once) to signal INACTIVE on page unload
      if (!pagehideHandler) {
        pagehideHandler = () => {
          // Close WebSocket cleanly on page unload
          eventsWebSocket?.close();
          if (
            currentSessionToken &&
            precomputedInactiveSha256 &&
            !isTabPaused
          ) {
            signalInactive(currentSessionToken, precomputedInactiveSha256);
          }
        };
        window.addEventListener("pagehide", pagehideHandler);
      }

      communication!.sendToWidget({
        type: "SESSION_CREATED",
        payload: { authResponse },
      });

      // Connect to AppSync Events WebSocket for real-time messages
      if (authResponse.eventsConfig) {
        connectEventsWebSocket(
          authResponse.eventsConfig,
          authResponse.sessionId
        );
      }

      debug.log("[Apten Loader] Session ready");
    } catch (error) {
      debug.error(
        "[Apten Loader] Session creation/restoration failed:",
        error
      );
      sessionCreated = false; // Allow retry

      communication!.sendToWidget({
        type: "SESSION_ERROR",
        payload: {
          message: error instanceof Error ? error.message : String(error),
          code: "SESSION_CREATION_FAILED",
        },
      });
    }
  });

  // Register other message handlers
  communication!.onMessage("RESIZE", (data: any) => {
    debug.log("[Apten Loader] Widget resize request:", data);
    if (iframeManager && data) {
      const isOpen =
        data.position === "expanded"
          ? true
          : data.position === "minimized"
          ? false
          : undefined;
      iframeManager.resize(data.width, data.height, isOpen, data.borderRadius);
    }
  });

  communication!.onMessage("CLOSE", () => {
    debug.log("[Apten Loader] Widget close request");
    cleanup();
  });

  communication!.onMessage("END_SESSION", async () => {
    debug.log("[Apten Loader] Session end request received");

    // Close WebSocket before ending session
    closeEventsWebSocket();

    // Use stored token for backend cleanup
    if (currentSessionToken && sessionCreated) {
      try {
        await endSession(currentSessionToken);
      } catch (error) {
        debug.warn("[Apten Loader] Failed to end session on backend:", error);
      }
    }

    // Clear stored token and cleanup
    currentSessionToken = null;
    clearSessionId();

    // Reset pagehide state (but keep BroadcastChannel alive for future sessions)
    if (pagehideHandler) {
      window.removeEventListener("pagehide", pagehideHandler);
      pagehideHandler = null;
    }
    isTabPaused = false;
    precomputedInactiveSha256 = null;
    sessionCreated = false; // Reset flag to allow new session creation

    // Send confirmation back to widget
    communication!.sendToWidget({
      type: "SESSION_ENDED",
      payload: {},
    });
  });

  // NEW: Handle token renewal requests from widget
  communication!.onMessage("REQUEST_TOKEN_RENEWAL", async (payload: any) => {
    const { sessionId, isEmergency, renewalId } = payload;

    debug.log(`[Apten Loader] Token renewal request received:`, {
      sessionId,
      isEmergency,
      renewalId,
    });

    try {
      // Use existing createSession function for renewal
      const renewedAuth = await createSession(config.apiKey, sessionId);

      // Update stored token
      currentSessionToken = renewedAuth.sessionToken;

      debug.log("[Apten Loader] ✅ Token renewal successful:", {
        sessionId: renewedAuth.sessionId,
        newSession: renewedAuth.newSession,
        tokenTtlMinutes: renewedAuth.tokenTtlMinutes,
      });

      // Send success response back to widget
      communication!.sendToWidget({
        type: "TOKEN_RENEWED",
        payload: {
          authResponse: renewedAuth,
          renewalId, // Include ID for response matching
        },
      });
    } catch (error) {
      debug.error("[Apten Loader] ❌ Token renewal failed:", error);

      // Send error response back to widget
      communication!.sendToWidget({
        type: "TOKEN_RENEWAL_ERROR",
        payload: {
          error: error instanceof Error ? error.message : String(error),
          code:
            error instanceof AuthenticationError
              ? error.status.toString()
              : "RENEWAL_FAILED",
          renewalId, // Include ID for response matching
        },
      });
    }
  });

  communication!.onMessage("ERROR", (error: any) => {
    debug.error("[Apten Loader] Widget error:", error);

    // Show error in development
    if (import.meta.env.VITE_DEBUG === "true") {
      const errorContainer = document.createElement("div");
      errorContainer.id = "apten-widget-error";
      errorContainer.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        background: #ff4444;
        color: white;
        padding: 10px;
        border-radius: 4px;
        font-size: 12px;
        z-index: 999999;
        max-width: 300px;
      `;
      errorContainer.textContent = `[DEV] Widget Error: ${error.message}`;
      document.body.appendChild(errorContainer);

      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (errorContainer.parentNode) {
          errorContainer.parentNode.removeChild(errorContainer);
        }
      }, 5000);
    }
  });

  // Initial handshake - send validation data
  communication!.onMessage("READY", () => {
    debug.log(
      "[Apten Loader] Widget ready, sending configuration and validation"
    );
    communication!.sendToWidget({
      type: "INIT",
      payload: {
        config,
        validation, // Send validation instead of full auth
        sessionRequired: true, // Indicate session not yet created
      },
    });
    // Seed the widget with the current viewport class so it can set
    // data-viewport before any resize events fire.
    communication!.sendToWidget({
      type: "VIEWPORT_CHANGE",
      payload: { isMobile: iframeManager!.isMobile() },
    });
  });
}

/**
 * Reinitialize the widget with current configuration
 */
async function reinitialize(): Promise<void> {
  const config = window.AptenWidget;
  if (config) {
    debug.log(
      "[Apten Loader] Reinitializing widget with current configuration"
    );
    await initializeWidget(config);
  } else {
    debug.warn(
      "[Apten Loader] No AptenWidget configuration found for reinitialization"
    );
  }
}

/**
 * Auto-initialize when DOM is ready
 */
function autoInitialize(): void {
  const config = window.AptenWidget;
  if (config) {
    debug.log("[Apten Loader] Auto-initializing widget");
    initializeWidget(config);
  } else {
    debug.warn("[Apten Loader] No AptenWidget configuration found");
  }
}

// Expose global API (same interface as before for compatibility)
window.AptenWidgetAPI = {
  initialize: initializeWidget,
  reinitialize,
  cleanup,
};

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", autoInitialize);
} else {
  autoInitialize();
}
